import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Users, Plus, Trash2, User } from "lucide-react";
import { toast } from "sonner";

const RELATIONS = ["spouse", "son", "daughter", "father", "mother", "brother", "sister", "other"];

export default function PatientFamily() {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", relation: "spouse", age: "", gender: "male", phone: "", email: "", blood_group: "" });

  const load = () => api.get("/family").then((r) => setMembers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/family", { ...form, age: form.age ? Number(form.age) : null });
      toast.success("Family member added");
      setOpen(false);
      setForm({ full_name: "", relation: "spouse", age: "", gender: "male", phone: "", email: "", blood_group: "" });
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this family member?")) return;
    try { await api.delete(`/family/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(apiError(err, "Delete failed")); }
  };

  return (
    <DashboardLayout role="patient">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><Users className="w-6 h-6 text-efgh-blue" /> Family members</h1>
          <p className="text-ink/60">Book tests and upload reports for your loved ones from a single account.</p>
        </div>
        <Button data-testid="add-family" onClick={() => setOpen(true)} className="bg-ink hover:bg-ink/90 text-white"><Plus className="w-4 h-4 mr-2" />Add member</Button>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {members.length === 0 && (
          <Card className="p-10 rounded-2xl text-center col-span-full">
            <Users className="w-8 h-8 mx-auto text-ink/30" />
            <div className="mt-3 font-heading font-semibold text-ink">No family members yet</div>
            <p className="text-sm text-ink/60 mt-1">Add your spouse, kids or parents to book tests and manage their reports.</p>
          </Card>
        )}
        {members.map((m) => (
          <Card key={m.id} className="p-5 rounded-2xl" data-testid={`family-${m.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-efgh-orange/20 via-efgh-yellow/20 to-efgh-green/20 grid place-items-center"><User className="w-5 h-5 text-ink/70" /></div>
              <button onClick={() => remove(m.id)} className="text-ink/40 hover:text-danger" data-testid={`del-family-${m.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-3 font-heading font-semibold text-ink">{m.full_name}</div>
            <div className="text-xs text-ink/50 capitalize">{m.relation}{m.age ? ` · ${m.age}y` : ""}{m.gender ? ` · ${m.gender}` : ""}</div>
            <div className="mt-3 space-y-1 text-xs text-ink/60">
              {m.phone && <div>📞 {m.phone}</div>}
              {m.email && <div>✉️ {m.email}</div>}
              {m.blood_group && <div>🩸 Blood group: {m.blood_group}</div>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="family-dialog">
          <DialogTitle className="font-heading">Add family member</DialogTitle>
          <form onSubmit={add} className="space-y-3">
            <div><Label>Full name</Label><Input required data-testid="fm-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Relation</Label>
                <Select value={form.relation} onValueChange={(v) => setForm({ ...form, relation: v })}>
                  <SelectTrigger data-testid="fm-relation"><SelectValue /></SelectTrigger>
                  <SelectContent>{RELATIONS.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Age</Label><Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} /></div>
              <div><Label>Blood group</Label><Input placeholder="e.g. B+" value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <Button type="submit" data-testid="fm-submit" className="w-full h-11 bg-ink hover:bg-ink/90 text-white">Add member</Button>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
